import { useEffect } from 'react';
import { Button, Card, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space, Spin, message } from 'antd';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SaleResult } from '@never2busy/shared';
import { salesApi, type SaleInput } from '../../../api/endpoints/sales';
import { AccountSelect } from '../../../components/common/AccountSelect';
import { PersonSelect } from '../../../components/common/PersonSelect';
import { ProjectSelect } from '../../../components/common/ProjectSelect';
import { UserSelect } from '../../../components/common/UserSelect';
import { LookupSelect } from '../../../components/common/LookupSelect';
import { PageHeader } from '../../../components/common/PageHeader';

const schema = z.object({
  name: z.string().min(1, 'Nombre obligatorio').max(120),
  saleNumber: z.string().max(40).optional().or(z.literal('')),
  product: z.string().max(120).optional().or(z.literal('')),
  stage: z.string().optional().or(z.literal('')),
  result: z.nativeEnum(SaleResult).optional(),
  probability: z.number().min(0).max(100).nullable().optional(),
  amount: z.string().optional().or(z.literal('')),
  saleDate: z.string().optional().or(z.literal('')),
  poNumber: z.string().max(40).optional().or(z.literal('')),
  accountId: z.number().int().nullable().optional(),
  personId: z.number().int().nullable().optional(),
  projectId: z.number().int().nullable().optional(),
  assignedToId: z.number().int().nullable().optional(),
  notes: z.string().optional().or(z.literal('')),
});

type FormValues = z.infer<typeof schema>;

function sanitize(values: FormValues): SaleInput {
  const out: Partial<SaleInput> = { name: values.name };
  if (values.saleNumber) out.saleNumber = values.saleNumber;
  if (values.product) out.product = values.product;
  if (values.stage) out.stage = values.stage;
  if (values.result) out.result = values.result;
  if (values.probability != null) out.probability = values.probability;
  if (values.amount) out.amount = values.amount;
  if (values.saleDate) out.saleDate = values.saleDate;
  if (values.poNumber) out.poNumber = values.poNumber;
  if (values.accountId != null) out.accountId = values.accountId;
  if (values.personId != null) out.personId = values.personId;
  if (values.projectId != null) out.projectId = values.projectId;
  if (values.assignedToId != null) out.assignedToId = values.assignedToId;
  if (values.notes) out.notes = values.notes;
  return out as SaleInput;
}

export function SaleEditPage(): JSX.Element {
  const { id } = useParams();
  const saleId = id ? parseInt(id, 10) : null;
  const isEdit = saleId !== null;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const presetAccountId = searchParams.get('accountId');
  const presetProjectId = searchParams.get('projectId');

  const { data, isLoading } = useQuery({
    queryKey: ['sale', saleId],
    queryFn: () => salesApi.findOne(saleId!),
    enabled: isEdit,
  });

  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      saleNumber: '',
      product: '',
      stage: 'PROSPECT',
      result: SaleResult.PENDING,
      probability: 50,
      amount: '',
      saleDate: '',
      poNumber: '',
      accountId: presetAccountId ? parseInt(presetAccountId, 10) : null,
      personId: null,
      projectId: presetProjectId ? parseInt(presetProjectId, 10) : null,
      assignedToId: null,
      notes: '',
    },
  });

  const watchedAccountId = useWatch({ control, name: 'accountId' });

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        saleNumber: data.saleNumber ?? '',
        product: data.product ?? '',
        stage: data.stage ?? '',
        result: data.result,
        probability: data.probability,
        amount: data.amount ?? '',
        saleDate: data.saleDate ?? '',
        poNumber: data.poNumber ?? '',
        accountId: data.accountId,
        personId: data.personId,
        projectId: data.projectId,
        assignedToId: data.assignedToId,
        notes: data.notes ?? '',
      });
    }
  }, [data, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload = sanitize(values);
      if (isEdit) return salesApi.update(saleId, payload);
      return salesApi.create(payload);
    },
    onSuccess: (saved) => {
      message.success(isEdit ? 'Venta actualizada' : 'Venta creada');
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
      void queryClient.invalidateQueries({ queryKey: ['sale', saved.id] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate(`/ventas/${saved.id}`);
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'No se pudo guardar la venta';
      message.error(msg);
    },
  });

  if (isEdit && isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={isEdit ? 'Editar venta' : 'Nueva venta'}
        crumbs={[
          { title: 'Ventas', to: '/ventas' },
          { title: isEdit ? data?.name ?? '' : 'Nueva' },
        ]}
      />

      <Form layout="vertical" onFinish={handleSubmit((v) => mutation.mutate(v))}>
        <Card title="Datos generales" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Controller
                name="saleNumber"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Número">
                    <Input {...field} placeholder="V-2026-001" />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={16}>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <Form.Item
                    label="Nombre / descripción"
                    required
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input {...field} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="product"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Producto / servicio">
                    <Input {...field} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={6}>
              <Controller
                name="stage"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Etapa">
                    <LookupSelect
                      listName="sale_stage"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={6}>
              <Controller
                name="result"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Resultado">
                    <Select
                      {...field}
                      options={[
                        { label: 'Pendiente', value: SaleResult.PENDING },
                        { label: 'Ganada', value: SaleResult.WON },
                        { label: 'Perdida', value: SaleResult.LOST },
                      ]}
                      onChange={field.onChange}
                    />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
        </Card>

        <Card title="Monto y probabilidad" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={12} md={8}>
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Monto">
                    <Input {...field} placeholder="0.00" inputMode="decimal" />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={12} md={8}>
              <Controller
                name="probability"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Probabilidad (%)">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={100}
                      value={field.value ?? undefined}
                      onChange={(v) => field.onChange(v ?? null)}
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={8}>
              <Controller
                name="saleDate"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Fecha esperada">
                    <DatePicker
                      style={{ width: '100%' }}
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(d) => field.onChange(d ? d.toISOString() : '')}
                      format="DD/MM/YYYY"
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="poNumber"
                control={control}
                render={({ field }) => (
                  <Form.Item label="No. orden de compra">
                    <Input {...field} />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
        </Card>

        <Card title="Vinculaciones" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Controller
                name="accountId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Cuenta">
                    <AccountSelect value={field.value} onChange={field.onChange} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="personId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Contacto">
                    <PersonSelect
                      value={field.value}
                      onChange={field.onChange}
                      accountId={watchedAccountId ?? undefined}
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="projectId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Proyecto">
                    <ProjectSelect
                      value={field.value}
                      onChange={field.onChange}
                      customerId={watchedAccountId ?? undefined}
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="assignedToId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Asignar a">
                    <UserSelect value={field.value} onChange={field.onChange} />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
        </Card>

        <Card title="Notas" style={{ marginBottom: 16 }}>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <Form.Item>
                <Input.TextArea {...field} rows={4} />
              </Form.Item>
            )}
          />
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" loading={mutation.isPending}>
            {isEdit ? 'Guardar cambios' : 'Crear venta'}
          </Button>
          <Button onClick={() => navigate(-1)}>Cancelar</Button>
        </Space>
      </Form>
    </>
  );
}
